/**
 * Normalize Shop catalog email-request API responses (200, no Quote records).
 *
 * @param {Object|null|undefined} data - Raw response body
 * @returns {{ corporate_demand_ref: string, suppliers: Array<{ supplier_id?: *, supplier_name?: string, item_count?: number, emailSent: boolean }> }}
 */
export function normalizeCatalogEmailResponse(data) {
  const suppliers = Array.isArray(data?.suppliers)
    ? data.suppliers.map(s => ({
        supplier_id: s.supplier_id,
        supplier_name: s.supplier_name ?? '',
        item_count: s.item_count,
        emailSent: Boolean(s.email_sent ?? s.supplier_email_sent),
      }))
    : [];

  return {
    corporate_demand_ref: data?.corporate_demand_ref ?? '',
    suppliers,
  };
}

/**
 * Build a short success message from normalized email response.
 *
 * @param {{ corporate_demand_ref: string, suppliers: Array }} normalized
 * @returns {string}
 */
/**
 * Stable key for merging supplier rows from catalog quote email responses.
 *
 * @param {{ supplier_id?: *, supplier_name?: string }} supplier
 * @returns {string}
 */
function catalogQuoteSupplierMergeKey(supplier) {
  if (supplier?.supplier_id != null && supplier.supplier_id !== '') {
    return `id:${supplier.supplier_id}`;
  }
  return `name:${supplier?.supplier_name ?? ''}`;
}

/**
 * Merge two normalized catalog quote email results (e.g. per-supplier sends).
 *
 * @param {{ corporate_demand_ref?: string, suppliers?: Array }|null} previous
 * @param {{ corporate_demand_ref?: string, suppliers?: Array }|null} next
 * @returns {{ corporate_demand_ref: string, suppliers: Array }}
 */
export function mergeCatalogQuoteEmailResults(previous, next) {
  const prev = previous ?? { corporate_demand_ref: '', suppliers: [] };
  const nxt = next ?? { corporate_demand_ref: '', suppliers: [] };

  const corporate_demand_ref =
    nxt.corporate_demand_ref?.trim() ||
    prev.corporate_demand_ref?.trim() ||
    '';

  const supplierMap = new Map();
  [...(prev.suppliers ?? []), ...(nxt.suppliers ?? [])].forEach(s => {
    supplierMap.set(catalogQuoteSupplierMergeKey(s), { ...s });
  });

  return {
    corporate_demand_ref,
    suppliers: [...supplierMap.values()],
  };
}

export function catalogEmailSuccessMessage(normalized) {
  const first = normalized.suppliers[0];
  const name = first?.supplier_name || 'supplier';
  const emailNote = first?.emailSent ? 'Email sent' : 'Email not sent';
  const ref = normalized.corporate_demand_ref
    ? ` Ref: ${normalized.corporate_demand_ref}.`
    : '';
  if (normalized.suppliers.length > 1) {
    const sent = normalized.suppliers.filter(s => s.emailSent).length;
    return `Quote request emails sent to ${normalized.suppliers.length} supplier(s) (${sent} sent).${ref}`;
  }
  return `Quote request email to ${name}. ${emailNote}.${ref}`;
}
